import React, { useState, useEffect } from 'react';
import { useDrag, useDrop } from 'react-dnd';

const PlanCard = ({ plan, tierIndex, dataSetIndex, movePlan, deletePlan, type, disableDrag, onDragStart, onDragEnd }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [{ isDragging }, drag] = useDrag({
    type: "plan",
    item: { tierIndex, dataSetIndex },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: !disableDrag,
    end: (item, monitor) => {
      if (onDragEnd) onDragEnd();
    },
  });

  // Call onDragStart when dragging begins
  useEffect(() => {
    if (isDragging && onDragStart) {
      onDragStart();
    }
  }, [isDragging, onDragStart]);

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: "plan",
    drop: (draggedItem) => {
      if (disableDrag) return;
      // Allow all movements: vertical, horizontal, and diagonal
      if (draggedItem.tierIndex !== tierIndex || draggedItem.dataSetIndex !== dataSetIndex) {
        movePlan(draggedItem.tierIndex, tierIndex, draggedItem.dataSetIndex, dataSetIndex);
      }
    },
    canDrop: (draggedItem) => {
      if (disableDrag) return false;
      // Allow drops to any position (not the same position as source)
      return draggedItem.dataSetIndex !== dataSetIndex || draggedItem.tierIndex !== tierIndex;
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  const getBorderColor = (type, index) => {
    const colors = {
      current: 'border-blue-400',
      renewal: 'border-green-400',
      alternative: [
        'border-yellow-400',
        'border-orange-400',
        'border-purple-400',
        'border-teal-400',
        'border-pink-400']
    };
    return type !== 'alternative' ? colors[type] : colors.alternative[(index - 2)%5] || 'border-gray-400';
  };

  const getDropZoneStyles = () => {
    let styles = `border rounded-md p-1 ${getBorderColor(type, dataSetIndex)} transition-all duration-200`;

    if (disableDrag) {
      styles += ' cursor-default';
    } else {
      styles += ' cursor-move';
    }

    if (isDragging) {
      styles += ' opacity-35';
    }

    if (isOver && canDrop) {
      styles += ' ring-2 ring-blue-400 ring-opacity-75';
    } else if (isOver && !canDrop) {
      styles += ' ring-2 ring-red-400 ring-opacity-75';
    }

    return styles;
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (window.confirm(`Are you sure you want to delete "${plan.planName}"?`)) {
      deletePlan(tierIndex, dataSetIndex);
    }
  };

  return (
    <div 
      ref={(node) => drag(drop(node))} 
      className={getDropZoneStyles()}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ position: 'relative' }}
    >
      <h3 className="font-medium text-gray-900">{plan.planName}</h3>
      {isHovered && !disableDrag && (
        <button
          onClick={handleDelete}
          className="absolute bottom-0 right-0 p-0.5 text-red-500 hover:text-red-700 transition-colors opacity-80"
          title="Delete plan"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
            <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default PlanCard; 